// react and js
import { Link as RouterLink } from "react-router-dom";
import React, { useState, useEffect } from "react";

// External library
import JSZip from "jszip";

// mui
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  AppBar,
  Typography,
  Grid,
  Modal,
  IconButton,
  Drawer,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DownloadIcon from "@mui/icons-material/Download";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import CloseIcon from "@mui/icons-material/Close";

// components
import ModalDialog from "../components/ModalDialog";
import Iconify from "../components/Iconify";

// hooks
import defines from "../hooks/defines";

// layouts

// pages

// theme
import { B1Small, B3Small, ModalActionsLoadingButton } from "../theme/styled";

// utils
import { ModuleDataToTxtData } from "../utils/moduleConversion";

// widgets

// ----------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------
const STORAGE_KEY = "expertAdvisorBuilderData";
const DEFAULT_REMINDER_TIME = 3600;
const IMPORT_TUTORIAL = "https://www.youtube.com/embed/NTbmngn-eUA";

// Notification types
const NOTIFICATION_TYPES = {
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error",
};

const DOWNLOAD_NOTE_HELP_IMAGE_1 = "/illustrations/converted/download_note/help_images_1.png";
const DOWNLOAD_NOTE_HELP_IMAGE_2 = "/illustrations/converted/download_note/help_images_2.png";
const DOWNLOAD_NOTE_HELP_IMAGE_3 = "/illustrations/converted/download_note/help_images_3.png";
const DOWNLOAD_NOTE_HELP_IMAGE_4 = "/illustrations/converted/download_note/help_images_4.png";
const DOWNLOAD_NOTE_HELP_IMAGE_5 = "/illustrations/converted/download_note/help_images_5.png";

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function PublicHeader() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  // States for notifications - Updated to support multiple types
  const [notification, setNotification] = useState({
    message: "",
    type: NOTIFICATION_TYPES.SUCCESS,
    show: false,
  });
  const [showReminderMessage, setShowReminderMessage] = useState(false);
  const [reminderTime] = useState(DEFAULT_REMINDER_TIME);
  const [lastExportTime, setLastExportTime] = useState(Date.now());
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Download dialog state
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // ----------------------------------------------------------------------
  // Helper Functions
  // ----------------------------------------------------------------------

  // Function to get modules data from localStorage
  const getModulesData = () => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        return JSON.parse(savedData);
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  // Function to create and download a text file
  const downloadTextFile = (content, filename) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Function to create and download a ZIP file
  const downloadZipFile = async (modules) => {
    const zip = new JSZip();

    modules.forEach((moduleData) => {
      const TxtData = ModuleDataToTxtData(moduleData);
      const moduleNumber = moduleData.basics.nNumber;
      const fileName = `Module${moduleNumber}.txt`;
      zip.file(fileName, TxtData);
    });

    const content = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(content);
    const link = document.createElement("a");
    link.href = url;
    link.download = "TradingModules.zip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Enhanced notification function that supports different types
  const showNotification = (message, type = NOTIFICATION_TYPES.SUCCESS) => {
    setNotification({
      message,
      type,
      show: true,
    });

    if (type === NOTIFICATION_TYPES.SUCCESS) {
      setLastExportTime(Date.now());
    }

    // Hide message after 5 seconds
    setTimeout(() => {
      setNotification((prev) => ({ ...prev, show: false }));
    }, 10000);
  };

  // Specific notification functions for convenience
  const showSuccessMessage = (message) => showNotification(message, NOTIFICATION_TYPES.SUCCESS);
  const showWarningMessage = (message) => showNotification(message, NOTIFICATION_TYPES.WARNING);
  const showErrorMessage = (message) => showNotification(message, NOTIFICATION_TYPES.ERROR);

  // Function to get notification background color based on type
  const getNotificationBackgroundColor = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return "#a8e6cf";
      case NOTIFICATION_TYPES.WARNING:
        return "#ffe79a";
      case NOTIFICATION_TYPES.ERROR:
        return "#ffd3b6";
      default:
        return "#cacacaff";
    }
  };

  // Function to dismiss reminder and reset timer
  const handleDismissReminder = () => {
    setShowReminderMessage(false);
    setLastExportTime(Date.now());
  };

  // Function to handle Getting Started button
  const handleGettingStarted = () => {
    window.open(`${process.env.REACT_APP_URL_FRONTEND_WEBSITE}/manuals/expert-advisor-builder`, "_blank");
    setIsMobileSidebarOpen(false);
  };

  // Actual download
  const startDownload = () => {
    window.location.href = `${process.env.REACT_APP_URL_API_DOWNLOAD}/?tag=expert_advisor_builder_zip `;
  };

  // Function to handle Download Local Setup download
  const handleRuntimeInstaller = () => {
    startDownload();

    // Open dialog and start download immediately
    setShowDownloadDialog(true);
  };

  // Function to close download dialog
  const handleCloseDownloadDialog = () => {
    setShowDownloadDialog(false);
  };

  // Function to handle export trading modules - Updated with better error handling
  const handleExportTradingModules = async () => {
    const modulesData = getModulesData();

    if (modulesData.length === 0) {
      showWarningMessage("No modules to export");
      setIsMobileSidebarOpen(false);
      return;
    }

    try {
      // Validate modules before export
      const invalidModules = modulesData.filter(
        (module) =>
          // Add your validation logic here
          !module.basics || !module.basics.nNumber
      );

      if (invalidModules.length > 0) {
        showErrorMessage(`${invalidModules.length} module(s) have invalid configuration and cannot be exported`);
        setIsMobileSidebarOpen(false);
        return;
      }

      if (modulesData.length === 1) {
        // Check if the single module has required data
        const moduleData = modulesData[0];
        if (!moduleData.basics || !moduleData.basics.nNumber) {
          showErrorMessage("Module configuration is incomplete. Please check your module settings.");
          setIsMobileSidebarOpen(false);
          return;
        }

        const TxtData = ModuleDataToTxtData(moduleData);
        const moduleNumber = moduleData.basics.nNumber;
        const fileName = `Module${moduleNumber}.txt`;

        downloadTextFile(TxtData, fileName);
        showSuccessMessage("Module exported successfully!");
      } else {
        // Check if any modules are missing required data
        const modulesWithIssues = modulesData.filter((module) => !module.basics || !module.basics.nNumber);

        if (modulesWithIssues.length > 0) {
          showWarningMessage(`${modulesWithIssues.length} module(s) have configuration issues but export will continue`);
        }

        // Multiple modules - export as ZIP file
        await downloadZipFile(modulesData);
        showSuccessMessage(`${modulesData.length} modules exported successfully!`);
      }
    } catch (error) {
      showErrorMessage("Critical error occurred during export. Please try again or contact support.");
    }
    setIsMobileSidebarOpen(false);
  };

  // Mobile sidebar handlers
  const handleMobileSidebarOpen = () => {
    setIsMobileSidebarOpen(true);
  };

  const handleMobileSidebarClose = () => {
    setIsMobileSidebarOpen(false);
  };

  // ----------------------------------------------------------------------
  // Reminder System
  // ----------------------------------------------------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      const modulesData = getModulesData();

      // Only show reminder and start timer if there's at least one module
      if (modulesData.length > 0) {
        const timeSinceLastExport = (Date.now() - lastExportTime) / 1000; // in seconds

        if (timeSinceLastExport >= reminderTime) {
          setShowReminderMessage(true);
        }
      } else {
        // If no modules, hide reminder and reset timer
        setShowReminderMessage(false);
        setLastExportTime(Date.now());
      }
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, [lastExportTime, reminderTime]);

  // Hide reminder message when export is performed
  useEffect(() => {
    if (notification.show && notification.type === NOTIFICATION_TYPES.SUCCESS) {
      setShowReminderMessage(false);
    }
  }, [notification]);

  // Close mobile sidebar when switching to desktop
  useEffect(() => {
    if (isDesktop && isMobileSidebarOpen) {
      setIsMobileSidebarOpen(false);
    }
  }, [isDesktop, isMobileSidebarOpen]);

  // Hide notifications when video modal is opened
  useEffect(() => {
    if (showVideoModal) {
      setNotification((prev) => ({ ...prev, show: false }));
    }
  }, [showVideoModal]);

  return (
    <>
      <AppBar
        sx={{
          background: "#F2F2F2",
          boxShadow: "0px 0px 2px rgba(0,0,0,0.5)",
          minHeight: isDesktop ? defines.headerHeightDesktop : defines.headerHeightMobile,
          maxHeight: isDesktop ? defines.headerHeightDesktop : defines.headerHeightMobile,
        }}
      >
        <Grid
          container
          sx={{
            alignItems: "center",
            height: isDesktop ? defines.headerHeightDesktop : defines.headerHeightMobile,
          }}
        >
          <Grid item>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <RouterLink
                to={`${process.env.REACT_APP_URL_FRONTEND_WEBSITE}/expert-advisor-builder`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                }}
              >
                {isDesktop ? (
                  <Box
                    component="img"
                    src={process.env.REACT_APP_PATH_ICON_SCALEABLE}
                    sx={{
                      marginTop: "-1px",
                      marginLeft: "22px",
                      width: "38px",
                      height: "38px",
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={process.env.REACT_APP_PATH_ICON_SCALEABLE}
                    sx={{
                      marginTop: "-2px",
                      marginLeft: "16px",
                      width: "38px",
                      height: "38px",
                    }}
                  />
                )}

                <Typography
                  sx={{
                    marginLeft: "12px",
                    fontSize: "22px",
                    fontWeight: 700,
                    letterSpacing: 0.15,
                    color: "#2E2E2E",
                    whiteSpace: "nowrap",
                  }}
                >
                  Expert Advisor Builder
                </Typography>
              </RouterLink>
            </Box>
          </Grid>

          <Grid item sx={{ flexGrow: 1 }} />

          {isDesktop ? (
            <Grid item sx={{ display: "flex", alignItems: "center", gap: 1, marginRight: "22px" }}>
              <B3Small disableRipple={Boolean(true)} variant="outlined" onClick={handleGettingStarted}>
                <MenuBookIcon fontSize="small" sx={{ mr: "5px" }} />
                Manual
              </B3Small>

              <B3Small disableRipple={Boolean(true)} variant="outlined" onClick={handleRuntimeInstaller}>
                <DownloadIcon fontSize="small" sx={{ mr: "5px" }} />
                Download local setup
              </B3Small>

              <B1Small onClick={handleExportTradingModules}>
                <AutoGraphIcon fontSize="small" sx={{ mr: "5px" }} />
                Export trading modules
              </B1Small>
            </Grid>
          ) : (
            <Grid item sx={{ display: "flex", alignItems: "center", marginRight: "12px" }}>
              <IconButton
                onClick={handleMobileSidebarOpen}
                sx={{
                  marginLeft: "-14px",
                  borderRadius: "10px",
                }}
              >
                <Iconify
                  icon="eva:menu-fill"
                  minWidth={35}
                  minHeight={35}
                  color="#2E2E2E"
                />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </AppBar>

      {/* Mobile Sidebar */}
      <Drawer
        anchor="right"
        open={isMobileSidebarOpen}
        onClose={handleMobileSidebarClose}
        PaperProps={{
          sx: {
            width: defines.sidebarWidthMobile,
            backgroundColor: "#F2F2F2",
            borderLeft: "1px solid #C8C8C8",
          },
        }}
      >
        <Box
          sx={{
            background: "#F2F2F2",
            display: "flex",
            minHeight: defines.headerHeightMobile,
            boxShadow: "0px 0px 2px rgba(0,0,0,0.5)",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            pr: "12px",
          }}
        >
          <Typography
            variant="contentBigBold"
            sx={{
              fontWeight: "bold",
              color: "#2E2E2E",
            }}
          >
            Menu
          </Typography>

          <IconButton
            onClick={handleMobileSidebarClose}
            sx={{ borderRadius: "10px" }}
          >
            <Iconify
              icon="eva:close-outline"
              minWidth={35}
              minHeight={35}
              color="#2E2E2E"
            />
          </IconButton>
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <B3Small
            disableRipple={Boolean(true)}
            variant="outlined"
            onClick={handleGettingStarted}
            sx={{
              width: "100%",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <MenuBookIcon fontSize="small" sx={{ mr: "8px" }} />
            Manual
          </B3Small>

          <B3Small
            disableRipple={Boolean(true)}
            variant="outlined"
            onClick={handleRuntimeInstaller}
            sx={{
              width: "100%",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <DownloadIcon fontSize="small" sx={{ mr: "8px" }} />
            Download local setup
          </B3Small>

          <B1Small
            onClick={handleExportTradingModules}
            sx={{
              width: "100%",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <AutoGraphIcon fontSize="small" sx={{ mr: "8px" }} />
            Export trading modules
          </B1Small>
        </Box>
      </Drawer>

      {/* Enhanced Notification Message - Now supports Success, Warning, and Error */}
      {notification.show && (
        <Box
          sx={{
            position: "fixed",
            bottom: "30px",
            // Desktop: right-aligned, Mobile: centered with margins
            ...(isDesktop ? { right: "30px" } : { left: "20px", right: "30px" }),
            backgroundColor: getNotificationBackgroundColor(notification.type),
            color: "inherit",
            padding: "8px 16px",
            borderRadius: "10px",
            zIndex: 10000,
            boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
            animation: "fadeInOut 10s ease-in-out forwards",
            "@keyframes fadeInOut": {
              "0%": { opacity: 0, transform: "translateY(20px)" },
              "10%": { opacity: 1, transform: "translateY(0)" },
              "90%": { opacity: 1, transform: "translateY(0)" },
              "100%": { opacity: 0, transform: "translateY(20px)" },
            },
          }}
          onAnimationEnd={() => setNotification((prev) => ({ ...prev, show: false }))}
        >
          <Box
            sx={{
              display: "flex",
              // Desktop: horizontal layout, Mobile: vertical centered layout
              flexDirection: isDesktop ? "row" : "column",
              alignItems: "center",
              gap: isDesktop ? 2 : 1,
              textAlign: isDesktop ? "left" : "center",
            }}
          >
            <Typography variant="contentMiddle">{notification.message}</Typography>
          </Box>
        </Box>
      )}

      {/* Enhanced Notification Message - Now supports Success, Warning, and Error */}
      {notification.show && (
        <Box
          sx={{
            position: "fixed",
            bottom: "80px",
            // Desktop: right-aligned, Mobile: centered with margins
            ...(isDesktop ? { right: "30px" } : { left: "20px", right: "30px" }),
            backgroundColor: getNotificationBackgroundColor(notification.type),
            color: "inherit",
            padding: "8px 16px",
            borderRadius: "10px",
            zIndex: 10000,
            boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
            animation: "fadeInOut 10s ease-in-out forwards",
            "@keyframes fadeInOut": {
              "0%": { opacity: 0, transform: "translateY(20px)" },
              "10%": { opacity: 1, transform: "translateY(0)" },
              "90%": { opacity: 1, transform: "translateY(0)" },
              "100%": { opacity: 0, transform: "translateY(20px)" },
            },
          }}
          onAnimationEnd={() => setNotification((prev) => ({ ...prev, show: false }))}
        >
          <Box
            sx={{
              display: "flex",
              // Desktop: horizontal layout, Mobile: vertical centered layout
              flexDirection: isDesktop ? "row" : "column",
              alignItems: "center",
              gap: isDesktop ? 2 : 1,
              textAlign: isDesktop ? "left" : "center",
            }}
          >
            <Typography
              variant="contentMiddle"
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
                "&:hover": { opacity: 0.8 },
              }}
              onClick={() => window.open(`${process.env.REACT_APP_URL_FRONTEND_WEBSITE}/mt5-tick-data/`, "_blank")}
            >
              Need quality tick data for strategy testing?
            </Typography>
          </Box>
        </Box>
      )}

      {/* Enhanced Notification Message */}
      {notification.show && IMPORT_TUTORIAL !== null && (
        <Box
          sx={{
            position: "fixed",
            bottom: "130px",
            // Desktop: right-aligned, Mobile: centered with margins
            ...(isDesktop ? { right: "30px" } : { left: "20px", right: "30px" }),
            backgroundColor: getNotificationBackgroundColor(notification.type),
            color: "inherit",
            padding: "8px 16px",
            borderRadius: "10px",
            zIndex: 10000,
            boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
            animation: "fadeInOut 10s ease-in-out forwards",
            "@keyframes fadeInOut": {
              "0%": { opacity: 0, transform: "translateY(20px)" },
              "10%": { opacity: 1, transform: "translateY(0)" },
              "90%": { opacity: 1, transform: "translateY(0)" },
              "100%": { opacity: 0, transform: "translateY(20px)" },
            },
          }}
          onAnimationEnd={() => setNotification((prev) => ({ ...prev, show: false }))}
        >
          <Box
            sx={{
              display: "flex",
              // Desktop: horizontal layout, Mobile: vertical centered layout
              flexDirection: isDesktop ? "row" : "column",
              alignItems: "center",
              gap: isDesktop ? 2 : 1,
              textAlign: isDesktop ? "left" : "center",
            }}
          >
            {IMPORT_TUTORIAL !== null && (
              <Typography
                variant="contentMiddle"
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontWeight: "bold",
                  "&:hover": { opacity: 0.8 },
                }}
                onClick={() => setShowVideoModal(true)}
              >
                Watch getting started video
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Video Modal */}
      <Modal
        open={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isDesktop ? "0px" : "20px",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: isDesktop ? "80vw" : "90vw",
            aspectRatio: "16/9",
            maxWidth: "1280px",
            maxHeight: "90vh",
            backgroundColor: "black",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <IconButton
            onClick={() => setShowVideoModal(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              zIndex: 1,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <iframe
            width="100%"
            height="100%"
            src={`${IMPORT_TUTORIAL}?autoplay=1`}
            title="MetaTrader import tutorial"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>
      </Modal>

      {/* Download Dialog */}
      <ModalDialog
        open={showDownloadDialog}
        handleClose={handleCloseDownloadDialog}
        scroll="paper"
        outlet={
          <>
            <DialogTitle
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Download local setup</Typography>
              <IconButton onClick={handleCloseDownloadDialog} sx={{ borderRadius: "10px" }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent
              dividers
              sx={{
                background: "#F2F2F2",
                borderTop: "1px solid #C8C8C8",
                borderBottom: "0px solid #C8C8C8",
                px: 2,
                pt: 2,
                pb: 1,
                width: "100%",
                minHeight: "200px",
                boxSizing: "border-box",
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h3">
                  <b>Installation</b>
                </Typography>
                <Typography variant="contentMiddle">
                  Once the download is complete, you'll have a ZIP file named <b>Expert Advisor Builder</b>. Locate it in your downloads folder and
                  open it to view its contents.
                </Typography>
                <Box
                  component="img"
                  src={DOWNLOAD_NOTE_HELP_IMAGE_1}
                  sx={{
                    display: "block",
                    alignSelf: "center",
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "5px",
                    boxShadow: "0 0 4px 1px rgba(0, 0, 0, 0.15)",
                  }}
                />
                <Typography variant="contentMiddle">
                  Inside the archive, run the <b>Install Expert Advisor Builder</b> script to begin the installation.
                </Typography>
                <Box
                  component="img"
                  src={DOWNLOAD_NOTE_HELP_IMAGE_2}
                  sx={{
                    display: "block",
                    alignSelf: "center",
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "5px",
                    boxShadow: "0 0 4px 1px rgba(0, 0, 0, 0.15)",
                  }}
                />
                <Typography variant="contentMiddle">
                  When you start the installer directly from the ZIP, Windows shows a <b>Compressed (zipped) Folders</b> notice. Simply click{" "}
                  <b>Run</b> to continue, as shown below. Alternatively, you can extract all files in advance and then start{" "}
                  <b>Install Expert Advisor Builder</b> from the extracted folder. Note that this step may look slightly different if you open the
                  archive with a third-party tool such as WinZip or WinRAR.
                </Typography>
                <Box
                  component="img"
                  src={DOWNLOAD_NOTE_HELP_IMAGE_3}
                  sx={{
                    display: "block",
                    alignSelf: "center",
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "5px",
                    boxShadow: "0 0 4px 1px rgba(0, 0, 0, 0.15)",
                  }}
                />
                <Typography variant="contentMiddle">
                  Because the installer script is not digitally signed, Windows displays an <b>Open File - Security Warning</b> that lists the
                  publisher as <b>Unknown Publisher</b>. This is expected, so click <b>Run</b> to proceed. If you'd like to check the file beforehand,
                  use the <b>Verify Publisher</b> shortcut included in the archive.
                </Typography>
                <Box
                  component="img"
                  src={DOWNLOAD_NOTE_HELP_IMAGE_4}
                  sx={{
                    display: "block",
                    alignSelf: "center",
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "5px",
                    boxShadow: "0 0 4px 1px rgba(0, 0, 0, 0.15)",
                  }}
                />
                <Typography variant="contentMiddle">
                  The <b>Expert Advisor Builder Setup</b> wizard then opens and installs the add-on for both MetaTrader 4 and MetaTrader 5. Click{" "}
                  <b>Next</b> and follow the on-screen steps to complete the installation.
                </Typography>
                <Box
                  component="img"
                  src={DOWNLOAD_NOTE_HELP_IMAGE_5}
                  sx={{
                    display: "block",
                    alignSelf: "center",
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "5px",
                    boxShadow: "0 0 4px 1px rgba(0, 0, 0, 0.15)",
                  }}
                />
              </Stack>
            </DialogContent>

            <DialogActions
              sx={{
                background: "#F2F2F2",
                p: 2,
                pt: 0,
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                <ModalActionsLoadingButton fullWidth size="large" variant="contained" onClick={handleCloseDownloadDialog} sx={{ mt: 1 }}>
                  Close
                </ModalActionsLoadingButton>
              </Box>
            </DialogActions>
          </>
        }
      />

      {/* Export Reminder Message */}
      {showReminderMessage && (
        <Box
          sx={{
            position: "fixed",
            bottom: "30px",
            // Desktop: right-aligned, Mobile: centered with margins
            ...(isDesktop ? { right: "30px" } : { left: "20px", right: "30px" }),
            backgroundColor: "#ffe79a", // Updated warning color
            color: "inherit",
            padding: "8px 16px",
            borderRadius: "10px",
            zIndex: 10000,
            boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              // Desktop: horizontal layout, Mobile: vertical centered layout
              flexDirection: isDesktop ? "row" : "column",
              alignItems: "center",
              gap: isDesktop ? 2 : 1,
              textAlign: isDesktop ? "left" : "center",
            }}
          >
            <Typography variant="contentMiddle">Don't forget to export your trading modules!</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Typography
                variant="contentMiddle"
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontWeight: "bold",
                  "&:hover": { opacity: 0.8 },
                }}
                onClick={handleExportTradingModules}
              >
                Export
              </Typography>
              <Typography
                variant="contentMiddle"
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontWeight: "bold",
                  "&:hover": { opacity: 0.8 },
                }}
                onClick={handleDismissReminder}
              >
                Dismiss
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
